import canUseDOM from '@/__helpers/can-use-dom';

type TResizeObserverCallback = (entry: ResizeObserverEntry, observer: ResizeObserver) => void;

type TResizeObserverOptions = {
	box: 'content-box' | 'border-box' | 'device-pixel-content-box';
};

class ResizeObserverWrapper {
	private callbacks = new Map<Element, TResizeObserverCallback[]>();
	private isRequestAnimationInProgress = false;
	private observer: ResizeObserver | null = null;

	private static instance: ResizeObserverWrapper | null = null;

	public static getObserver() {
		if (!ResizeObserverWrapper.instance) {
			ResizeObserverWrapper.instance = new ResizeObserverWrapper();
		}

		return ResizeObserverWrapper.instance;
	}

	constructor() {
		this.observer = canUseDOM() ? new ResizeObserver(this.runCallbacks.bind(this)) : null;
	}

	public unsubscribe(target: Element, callback: TResizeObserverCallback) {
		if (!this.observer) return;

		const targetCallbacks = this.callbacks.get(target) ?? [];
		if (!targetCallbacks.length) return;

		const index = targetCallbacks.indexOf(callback);
		if (index !== -1) {
			targetCallbacks.splice(index, 1);
		}

		if (targetCallbacks.length === 0) {
			this.callbacks.delete(target);
			this.observer?.unobserve(target);
		}
	}

	public subscribe(target: Element, callback: TResizeObserverCallback, options?: TResizeObserverOptions) {
		if (!this.observer) return;

		if (!this.callbacks.has(target)) {
			this.callbacks.set(target, [callback]);
			this.observer?.observe(target, options);
		} else {
			this.callbacks.get(target)?.push(callback);
		}
	}

	private runCallbacks(entries: ResizeObserverEntry[], observer: ResizeObserver) {
		if (this.isRequestAnimationInProgress) return;

		this.isRequestAnimationInProgress = true;
		requestAnimationFrame(() => {
			entries.forEach((entry) => {
				const targetCallbacks = this.callbacks.get(entry.target);
				targetCallbacks?.forEach((callback) => callback(entry, observer));
			});
			this.isRequestAnimationInProgress = false;
		});
	}
}

export default ResizeObserverWrapper;
export type { TResizeObserverCallback, TResizeObserverOptions };
